import { authenticatedFetch } from './authenticatedFetch';
import { formatApiErrorMessage } from '../utils/apiErrorMessage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const FAVORITE_TYPE_SYSTEM = 'system';
export const FAVORITE_TYPE_USER = 'user';

function normalizeFavoriteItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const type = String(raw.type ?? '').trim().toLowerCase();
  const id = String(raw.id ?? '').trim();
  if ((type !== FAVORITE_TYPE_SYSTEM && type !== FAVORITE_TYPE_USER) || !id) {
    return null;
  }
  return { type, id };
}

function normalizeFavoriteList(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.favorite_models)
      ? data.favorite_models
      : [];

  const seen = new Set();
  const items = [];

  for (const raw of list) {
    const item = normalizeFavoriteItem(raw);
    if (!item) continue;
    const key = `${item.type}:${item.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push(item);
  }

  return items;
}

/**
 * GET /favorite-models — only call when favorites UI is shown.
 */
export async function listFavoriteModels() {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/favorite-models`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        /* ignore */
      }
      return {
        success: false,
        error: formatApiErrorMessage(errorBody, 'Erro ao carregar favoritos.'),
      };
    }

    const data = await response.json();
    return {
      success: true,
      data: { items: normalizeFavoriteList(data) },
    };
  } catch (error) {
    console.error('listFavoriteModels', error);
    return {
      success: false,
      error: formatApiErrorMessage(error, 'Erro ao carregar favoritos.'),
    };
  }
}

/**
 * PUT /favorite-models — replaces the full favorites list.
 * @param {{ type: string, id: string }[]} favoriteModels
 */
export async function saveFavoriteModels(favoriteModels) {
  try {
    const payload = {
      favorite_models: normalizeFavoriteList(favoriteModels),
    };

    const response = await authenticatedFetch(`${API_BASE_URL}/favorite-models`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        /* ignore */
      }
      return {
        success: false,
        error: formatApiErrorMessage(errorBody, 'Erro ao salvar favoritos.'),
      };
    }

    const data = await response.json();
    return {
      success: true,
      data: { items: normalizeFavoriteList(data) },
    };
  } catch (error) {
    console.error('saveFavoriteModels', error);
    return {
      success: false,
      error: formatApiErrorMessage(error, 'Erro ao salvar favoritos.'),
    };
  }
}

export function favoriteKey(type, id) {
  return `${type}:${id}`;
}
